import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, NavLink as RouterNavLink, useLocation } from "react-router-dom";
import {
  Activity,
  BookOpen,
  CalendarDays,
  ChevronDown,
  CircleDollarSign,
  Flag,
  Home,
  Map,
  Newspaper,
  ScrollText,
  Settings,
  Shield,
  ShoppingBag,
  Swords,
  Trophy,
  UserRound,
  Users,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../lib/cn";
import type { NavLink } from "./navModel";

/**
 * NavMenu — menu déroulant ornementé de la barre principale (desktop).
 *
 * Interaction : ouvre au survol ET au clic/focus clavier ; ferme sur Échap,
 * clic extérieur, ou perte de focus. Le déclencheur porte `aria-haspopup` +
 * `aria-expanded` ; le panneau est une liste de liens navigables au clavier
 * (flèches ↑/↓, Home/End). Respecte `prefers-reduced-motion` via l'anim CSS.
 *
 * Générique : accepte soit un `NavMenuModel` (menu public à sous-liens riches),
 * soit une liste `NavLink[]` brute (menus « Mon espace » / « Administration »).
 */

interface NavMenuProps {
  /** identifiant stable pour aria. */
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  links: NavLink[];
  /** lien chapeau optionnel (le libellé du menu y renvoie). */
  to?: string;
  /** aligne le panneau à droite du déclencheur (menus de fin de barre). */
  align?: "left" | "right";
  /** largeur du panneau (par défaut auto ~ 18rem). */
  panelClassName?: string;
  /** badge/décoration optionnel accolé au libellé (ex pastille de rôle). */
  trailing?: ReactNode;
}

export function NavMenu({
  id,
  label,
  links,
  to,
  align = "left",
  panelClassName,
  trailing,
}: NavMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLLIElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const menuId = `${id}-${useId()}`;
  const location = useLocation();

  const active = links.some((l) => matchPath(location.pathname, l.to));

  const clearClose = useCallback(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  }, []);

  const scheduleClose = useCallback(() => {
    clearClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  }, [clearClose]);

  // Ferme au changement de route.
  useEffect(() => setOpen(false), [location.pathname]);

  // Ferme sur clic extérieur.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const focusItem = useCallback((index: number) => {
    const items = panelRef.current?.querySelectorAll<HTMLElement>("[data-navitem]");
    if (!items || items.length === 0) return;
    const i = (index + items.length) % items.length;
    items[i]?.focus();
  }, []);

  const onTriggerKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
      window.setTimeout(() => focusItem(0), 0);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const onPanelKey = (e: React.KeyboardEvent) => {
    const items = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>("[data-navitem]") ?? [],
    );
    const current = items.indexOf(document.activeElement as HTMLElement);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusItem(current + 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        focusItem(current - 1);
        break;
      case "Home":
        e.preventDefault();
        focusItem(0);
        break;
      case "End":
        e.preventDefault();
        focusItem(items.length - 1);
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        btnRef.current?.focus();
        break;
      case "Tab":
        setOpen(false);
        break;
    }
  };

  const TriggerTag = to ? Link : "button";
  const TriggerIcon = NAV_ICONS[id] ?? Flag;

  return (
    <li
      ref={rootRef}
      className="relative flex h-full items-stretch"
      onMouseEnter={() => {
        clearClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <TriggerTag
        // @ts-expect-error — union Link/button : `to` n'est présent que pour Link.
        to={to}
        ref={btnRef as never}
        type={to ? undefined : "button"}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onKeyDown={onTriggerKey}
        onClick={(e: React.MouseEvent) => {
          // Sans lien chapeau, le clic bascule le menu ; avec, on laisse naviguer.
          if (!to) {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className={cn(
          "group relative flex h-[72px] items-center gap-2 border-r border-iron-400/45 px-4 text-xs font-semibold uppercase tracking-[0.08em] outline-none transition-colors duration-200",
          "after:absolute after:inset-x-3 after:bottom-0 after:h-[3px] after:bg-arcane-300 after:transition-opacity after:duration-200",
          active || open
            ? "bg-iron-600/45 text-arcane-100 after:opacity-100"
            : "text-parchment-200/65 after:opacity-0 hover:bg-iron-600/35 hover:text-parchment-50 hover:after:opacity-40",
        )}
      >
        <TriggerIcon className="h-4 w-4 shrink-0" />
        <span>{label}</span>
        {trailing}
        <Chevron open={open} />
      </TriggerTag>

      {open && (
        <div
          ref={panelRef}
          id={menuId}
          role="menu"
          aria-label={typeof label === "string" ? label : id}
          onKeyDown={onPanelKey}
          onMouseEnter={clearClose}
          onMouseLeave={scheduleClose}
          className={cn(
            // Panneau OPAQUE (bg-obsidian-400 plein) : au-dessus du héros doré, la moindre
            // transparence laissait lire le titre au travers. shadow + blur en renfort.
            "absolute top-full z-50 mt-1 w-80 origin-top animate-menu-in overflow-hidden rounded border border-iron-300/60 bg-obsidian-400 p-2 shadow-dropdown",
            align === "right" ? "right-0" : "left-0",
            panelClassName,
          )}
        >
          <ul className="relative flex flex-col gap-0.5">
            {links.map((l) => {
              const LinkIcon = iconForRoute(l.to);
              return <li key={l.to} role="none">
                <RouterNavLink
                  to={l.to}
                  data-navitem
                  role="menuitem"
                  end={l.to === "/" || l.to === "/wiki"}
                  className={({ isActive }) =>
                    cn(
                      "group/item flex items-start gap-3 rounded px-3 py-2.5 outline-none transition-all duration-150 ease-brass",
                      isActive
                        ? "bg-arcane-600/20 text-arcane-50 shadow-[inset_3px_0_0_0_#44b6a4]"
                        : "text-parchment-200/75 hover:bg-iron-500/35 hover:text-parchment-50 focus-visible:bg-iron-500/40",
                    )
                  }
                >
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center border border-iron-300/50 bg-obsidian-300 text-arcane-200 transition-colors group-hover/item:border-arcane-300/60">
                    <LinkIcon className="h-3.5 w-3.5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-display text-sm tracking-wide">{l.label}</span>
                    {l.desc && (
                      <span className="mt-0.5 block truncate text-xs text-parchment-200/45">{l.desc}</span>
                    )}
                  </span>
                </RouterNavLink>
              </li>;
            })}
          </ul>
        </div>
      )}
    </li>
  );
}

/** Chevron animé du déclencheur de menu. */
function Chevron({ open }: { open: boolean }) {
  return (
    <ChevronDown className={cn("h-3 w-3 opacity-60 transition-transform duration-200 ease-brass", open && "rotate-180")} aria-hidden />
  );
}

const NAV_ICONS: Record<string, LucideIcon> = {
  accueil: Home,
  actualites: Newspaper,
  guildes: Users,
  wiki: BookOpen,
  boutique: ShoppingBag,
  espace: UserRound,
  admin: Settings,
};

function iconForRoute(to: string): LucideIcon {
  if (to === "/") return Home;
  if (to.includes("evenement")) return CalendarDays;
  if (to.includes("patchnote") || to.includes("monde") || to.includes("forum")) return Newspaper;
  if (to.includes("guilde") || to.includes("classement")) return Trophy;
  if (to.includes("armurerie") || to.includes("classe")) return Swords;
  if (to.includes("carte")) return Map;
  if (to.includes("boutique") || to.includes("shop")) return ShoppingBag;
  if (to.includes("vote")) return CircleDollarSign;
  if (to.includes("statut")) return Activity;
  if (to.includes("reglement") || to.includes("guide")) return ScrollText;
  return Shield;
}

/** Correspondance de chemin tolérante (préfixe pour les sous-arbres). */
function matchPath(pathname: string, to: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(to + "/");
}
